/**
 * SIP Trunk 對接模式 — 後台 UI（Phase 5）
 *
 *   <PbxModeSelector>      PBX 卡片頂部的「對接方式」radio（extension / trunk）
 *                          僅 super_admin / dealer 可切；business 唯讀。
 *                          ext→trunk 切換必經 <CompatChecklistModal>（4 必勾 + 1 選勾）。
 *   <TrunkSettingsCard>    trunk 連線設定（取代分機清單編輯器）：
 *                          authMode / 帳密 / channels / 席次雙池 / CallerID / DID 清單 /
 *                          預設角色 / 轉真人模式
 *
 * 資料模型見 docs/sip-trunk-implementation-plan.md §1.1。
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, PhoneForwarded, Server } from 'lucide-react';
import { avatarsApi } from '../api/tenants.js';
import { SectionHeader } from './ui/PageHeader.jsx';
import { Card } from './ui/Card.jsx';
import { Input } from './ui/Input.jsx';
import { Button } from './ui/Button.jsx';

// trunk 預設值（切換時帶入；channels 故意留空逼使用者填真實值）
export function defaultTrunkConfig() {
    return {
        authMode: 'ip',
        username: '',
        password: '',
        channels: null,
        seats: { plan_01: 0, plan_02: 0 },
        callerIdMode: 'pbx',
        callerId: '',
        dids: [],
        defaultAvatarId: '',
        internalDialPrefix: '',
        transferMode: 'confbridge',
    };
}

// ────────────────────────────────────────────────────────────
// 對接方式選擇（PBX 卡片頂部）
// ────────────────────────────────────────────────────────────
export function PbxModeSelector({ mode, canEdit, grantedHint = false, onSwitchToTrunk, onSwitchToExtension }) {
    const { t } = useTranslation('tenant');
    const isTrunk = mode === 'trunk';

    if (!canEdit) {
        return (
            <div className="mb-4 pb-4 border-b border-cream-200">
                <div className="text-xs text-ink-500 mb-1">{t('trunk.mode.label')}</div>
                <div className="text-sm text-ink-800 font-medium">
                    {isTrunk ? t('trunk.mode.trunk') : t('trunk.mode.extension')}
                </div>
                <p className="text-xs text-ink-400 mt-1">{t('trunk.mode.contactToChange')}</p>
            </div>
        );
    }

    const opts = [
        {
            key: 'extension', label: t('trunk.mode.extension'), desc: t('trunk.mode.extensionDesc'),
            active: !isTrunk, onPick: () => { if (isTrunk) onSwitchToExtension(); },
        },
        {
            key: 'trunk', label: t('trunk.mode.trunk'), desc: t('trunk.mode.trunkDesc'),
            active: isTrunk, onPick: () => { if (!isTrunk) onSwitchToTrunk(); },
        },
    ];
    return (
        <div className="mb-4 pb-4 border-b border-cream-200">
            <div className="text-xs text-ink-500 mb-2">{t('trunk.mode.label')}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {opts.map((o) => (
                    <button
                        key={o.key}
                        type="button"
                        onClick={o.onPick}
                        className={[
                            'text-left px-3 py-2.5 rounded-md border transition',
                            o.active
                                ? 'border-accent-500 bg-accent-50 ring-1 ring-accent-300/50'
                                : 'border-cream-300 bg-white hover:border-cream-400',
                        ].join(' ')}
                    >
                        <div className="flex items-center gap-2">
                            <span className={[
                                'inline-block size-3 rounded-full border-4',
                                o.active ? 'border-accent-500 bg-white' : 'border-cream-300 bg-white',
                            ].join(' ')} />
                            <span className="text-sm font-medium text-ink-900">{o.label}</span>
                        </div>
                        <p className="text-xs text-ink-500 mt-1 ml-5">{o.desc}</p>
                    </button>
                ))}
            </div>
            <p className="text-xs text-ink-400 mt-2 flex items-center gap-1">
                <AlertTriangle size={12} className="shrink-0" /> {t('trunk.mode.switchWarnShort')}
            </p>
            {/* 商用帳號本來唯讀；被超管特例開通才會走到這裡 → 明講一句，免得客戶以為人人都能切 */}
            {grantedHint && (
                <p className="text-xs text-accent-700 mt-1">{t('trunk.mode.selfServiceGranted')}</p>
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────
// 相容性檢查清單（ext→trunk 切換閘門；即 Phase 6 切換精靈的核心步驟）
// 四項必勾 —— 沒跟客戶 PBX 團隊確認就切，會在上線當天才發現轉不了真人。
// ────────────────────────────────────────────────────────────
export function CompatChecklistModal({ tenant, onCancel, onConfirm }) {
    const { t } = useTranslation('tenant');
    const REQUIRED = ['trunkSupported', 'internalExtAddressable', 'trunkToPstnTransfer', 'callerIdConfirmed'];
    const [checks, setChecks] = useState({
        trunkSupported: false,          // PBX 支援 trunk 對接 + 已確認通道數
        internalExtAddressable: false,  // trunk 可直接尋址內線分機（轉真人）
        trunkToPstnTransfer: false,     // 允許 trunk → 外線（轉手機）
        callerIdConfirmed: false,       // CallerID 行為已確認（可指定 或 接受 PBX 固定）
        referSupported: false,          // （選）trunk 上可發 REFER + Replaces
    });
    const extCount = (tenant?.ipPhones || []).length;
    const allRequired = REQUIRED.every((k) => checks[k]);

    const items = [
        { key: 'trunkSupported', required: true },
        { key: 'internalExtAddressable', required: true },
        { key: 'trunkToPstnTransfer', required: true },
        { key: 'callerIdConfirmed', required: true },
        { key: 'referSupported', required: false },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-xl w-full p-6 max-h-[85vh] overflow-y-auto">
                <h3 className="heading-display text-lg font-semibold text-ink-900 flex items-center gap-2">
                    <PhoneForwarded size={18} className="text-accent-500" /> {t('trunk.checklist.title')}
                </h3>
                <p className="text-sm text-ink-600 mt-2">{t('trunk.checklist.intro')}</p>

                {extCount > 0 && (
                    <div className="mt-3 px-3 py-2.5 bg-warn-50 border border-warn-200 rounded-md text-xs text-warn-700 leading-relaxed">
                        <AlertTriangle size={13} className="inline mr-1 -mt-0.5" />
                        {t('trunk.checklist.extWarning', { n: extCount })}
                    </div>
                )}

                <div className="mt-4 space-y-2.5">
                    {items.map(({ key, required }) => (
                        <label key={key} className="flex items-start gap-2.5 cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={checks[key]}
                                onChange={(e) => setChecks((c) => ({ ...c, [key]: e.target.checked }))}
                                className="mt-0.5 accent-accent-500 size-4 shrink-0"
                            />
                            <span className="text-sm text-ink-700 leading-snug">
                                {t(`trunk.checklist.${key}`)}
                                {!required && <span className="text-ink-400 text-xs ml-1">{t('trunk.checklist.optionalTag')}</span>}
                            </span>
                        </label>
                    ))}
                </div>
                <p className="text-xs text-ink-400 mt-3">{t('trunk.checklist.referNote')}</p>

                <div className="mt-5 flex justify-end gap-2">
                    <Button variant="ghost" onClick={onCancel}>{t('trunk.checklist.cancel')}</Button>
                    <Button variant="accent" disabled={!allRequired} onClick={() => onConfirm(checks)}>
                        {t('trunk.checklist.confirm')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ────────────────────────────────────────────────────────────
// Trunk 連線設定卡（trunk 模式時取代分機清單編輯器）
// ────────────────────────────────────────────────────────────
export function TrunkSettingsCard({ hostId, trunk, onChange, dirty, status, onSave, platformIp, recordingOn = false }) {
    const { t } = useTranslation('tenant');
    const tr = trunk || defaultTrunkConfig();
    const [avatars, setAvatars] = useState([]);
    const [showPw, setShowPw] = useState(false);
    const [didInput, setDidInput] = useState('');
    const [copiedAddr, setCopiedAddr] = useState(false);
    const ourAddr = `${platformIp || '43.213.126.202'}:5060`;
    async function copyAddr() {
        try {
            await navigator.clipboard.writeText(ourAddr);
            setCopiedAddr(true);
            setTimeout(() => setCopiedAddr(false), 1600);
        } catch {}
    }

    useEffect(() => {
        let cancelled = false;
        avatarsApi.listForTenant(hostId)
            .then((r) => { if (!cancelled) setAvatars(r.avatars || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [hostId]);

    function up(patch) { onChange({ ...tr, ...patch }); }

    const channels = Number(tr.channels) || 0;
    const seatSum = (Number(tr.seats?.plan_01) || 0) + (Number(tr.seats?.plan_02) || 0);
    const seatMismatch = channels > 0 && seatSum !== channels;

    function addDid() {
        const v = didInput.trim().replace(/[^0-9+]/g, '');
        if (!v) return;
        if ((tr.dids || []).includes(v)) { setDidInput(''); return; }
        up({ dids: [...(tr.dids || []), v] });
        setDidInput('');
    }

    return (
        <Card>
            <SectionHeader title={t('trunk.card.title')} description={t('trunk.card.description')}>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-cream-100 text-xs text-ink-600">
                    <Server size={12} /> {t('trunk.card.badge', { n: channels || '?' })}
                </span>
            </SectionHeader>

            {/* 我方 Trunk 對接資訊 —— 客戶 PBX 團隊在他們那端設定 trunk 時要用的目標位址。
                沒有這塊，對方不知道要把 trunk 指到哪 / 防火牆放行什麼（2026-07-23 用戶回饋補上） */}
            <div className="mb-5 px-4 py-3 bg-accent-50 border border-accent-300/50 rounded-md">
                <div className="text-sm font-medium text-ink-800">{t('trunk.ourSide.title')}</div>
                <p className="text-xs text-ink-500 mt-1 leading-relaxed">{t('trunk.ourSide.description')}</p>
                <div className="mt-2.5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-ink-500 shrink-0">{t('trunk.ourSide.sipAddr')}</span>
                        <code className="font-mono text-sm font-semibold text-ink-900 bg-white px-2 py-1 rounded border border-cream-300 select-all">{ourAddr}</code>
                        <span className="text-ink-400 font-mono">UDP</span>
                        <button type="button" onClick={copyAddr} className="text-accent-600 hover:text-accent-500 text-xs underline-offset-2 hover:underline">
                            {copiedAddr ? t('whitelist.copied') : t('whitelist.copy')}
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-ink-500 shrink-0">{t('trunk.ourSide.rtp')}</span>
                        <code className="font-mono text-ink-900 bg-white px-2 py-1 rounded border border-cream-300 select-all">UDP 10000-20000</code>
                    </div>
                </div>
                <p className="text-xs text-ink-500 mt-2 leading-relaxed">
                    {(tr.authMode || 'ip') === 'register' ? t('trunk.ourSide.hintRegister') : t('trunk.ourSide.hintIp')}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* authMode */}
                <div>
                    <label className="block text-xs text-ink-500 mb-1">{t('trunk.authMode.label')}</label>
                    <select
                        value={tr.authMode || 'ip'}
                        onChange={(e) => up({ authMode: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-cream-300 rounded-md text-sm focus:outline-none focus:border-accent-500"
                    >
                        <option value="ip">{t('trunk.authMode.ip')}</option>
                        <option value="register">{t('trunk.authMode.register')}</option>
                    </select>
                    <p className="text-xs text-ink-400 mt-1">{t('trunk.authMode.hint')}</p>
                </div>

                {/* channels */}
                <Input
                    label={t('trunk.channels.label')}
                    type="number"
                    value={tr.channels ?? ''}
                    onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        up({ channels: Number.isFinite(n) && n > 0 ? n : null });
                    }}
                    placeholder="8"
                    mono
                    hint={t('trunk.channels.hint')}
                />

                {/* register 帳密 */}
                {tr.authMode === 'register' && (
                    <>
                        <Input
                            label={t('trunk.username.label')}
                            value={tr.username || ''}
                            onChange={(e) => up({ username: e.target.value.trim() })}
                            placeholder="qubbytrunk"
                            mono
                        />
                        <div>
                            <label className="block text-xs text-ink-500 mb-1">{t('trunk.password.label')}</label>
                            <div className="flex gap-2">
                                <input
                                    type={showPw ? 'text' : 'password'}
                                    value={tr.password || ''}
                                    onChange={(e) => up({ password: e.target.value })}
                                    className="flex-1 px-3 py-2 bg-white border border-cream-300 rounded-md font-mono text-sm focus:outline-none focus:border-accent-500"
                                />
                                <Button variant="ghost" size="sm" onClick={() => setShowPw((s) => !s)}>
                                    {showPw ? t('ipPhones.row.hide') : t('ipPhones.row.show')}
                                </Button>
                            </div>
                        </div>
                    </>
                )}

                {/* CallerID */}
                <div>
                    <label className="block text-xs text-ink-500 mb-1">{t('trunk.callerIdMode.label')}</label>
                    <select
                        value={tr.callerIdMode || 'pbx'}
                        onChange={(e) => up({ callerIdMode: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-cream-300 rounded-md text-sm focus:outline-none focus:border-accent-500"
                    >
                        <option value="pbx">{t('trunk.callerIdMode.pbx')}</option>
                        <option value="custom">{t('trunk.callerIdMode.custom')}</option>
                    </select>
                    <p className="text-xs text-ink-400 mt-1">{t('trunk.callerIdMode.hint')}</p>
                </div>
                {tr.callerIdMode === 'custom' && (
                    <Input
                        label={t('trunk.callerId.label')}
                        value={tr.callerId || ''}
                        onChange={(e) => up({ callerId: e.target.value.replace(/[^0-9+*#]/g, '') })}
                        placeholder="0277027154"
                        mono
                        hint={t('trunk.callerId.hint')}
                    />
                )}

                {/* 轉真人模式 */}
                <div>
                    <label className="block text-xs text-ink-500 mb-1">{t('trunk.transferMode.label')}</label>
                    <select
                        value={tr.transferMode || 'confbridge'}
                        onChange={(e) => up({ transferMode: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-cream-300 rounded-md text-sm focus:outline-none focus:border-accent-500"
                    >
                        <option value="confbridge">{t('trunk.transferMode.confbridge')}</option>
                        <option value="refer">{t('trunk.transferMode.refer')}</option>
                        <option value="bridge">{t('trunk.transferMode.bridge')}</option>
                    </select>
                    {recordingOn && tr.transferMode === 'refer' ? (
                        <p className="text-xs text-warn-600 font-medium mt-1">{t('trunk.transferMode.recordingConflict')}</p>
                    ) : (
                        <p className="text-xs text-ink-400 mt-1">{t('trunk.transferMode.hint')}</p>
                    )}
                </div>

                {/* 內線前綴（選填） */}
                <Input
                    label={t('trunk.internalDialPrefix.label')}
                    value={tr.internalDialPrefix || ''}
                    onChange={(e) => up({ internalDialPrefix: e.target.value.trim() })}
                    placeholder=""
                    mono
                    hint={t('trunk.internalDialPrefix.hint')}
                />
            </div>

            {/* 席次雙池（一條通道 = 一席，總和必須 = channels） */}
            <div className="mt-5 pt-4 border-t border-cream-100">
                <div className="text-xs text-ink-500 mb-2">{t('trunk.seats.label')}</div>
                <div className="grid grid-cols-2 gap-4 max-w-md">
                    {['plan_01', 'plan_02'].map((k) => (
                        <Input
                            key={k}
                            label={k === 'plan_01' ? t('trunk.seats.plan01') : t('trunk.seats.plan02')}
                            type="number"
                            value={tr.seats?.[k] ?? 0}
                            onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                up({ seats: { ...(tr.seats || {}), [k]: Number.isFinite(n) && n >= 0 ? n : 0 } });
                            }}
                            mono
                        />
                    ))}
                </div>
                <p className={['text-xs mt-2', seatMismatch ? 'text-warn-600 font-medium' : 'text-ink-400'].join(' ')}>
                    {seatMismatch
                        ? t('trunk.seats.mismatch', { sum: seatSum, channels })
                        : t('trunk.seats.hint')}
                </p>
                <p className="text-xs text-ink-400 mt-1">{t('trunk.seats.transferNote')}</p>
            </div>

            {/* 進線 DID 清單 */}
            <div className="mt-5 pt-4 border-t border-cream-100">
                <div className="text-xs text-ink-500 mb-1">{t('trunk.dids.label')}</div>
                <p className="text-xs text-ink-400 mb-2">{t('trunk.dids.hint')}</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {(tr.dids || []).map((d) => (
                        <span key={d} className="inline-flex items-center gap-1 px-2 py-1 bg-cream-100 rounded font-mono text-xs text-ink-700">
                            {d}
                            <button
                                type="button"
                                className="text-ink-400 hover:text-danger-500"
                                onClick={() => up({ dids: (tr.dids || []).filter((x) => x !== d) })}
                            >×</button>
                        </span>
                    ))}
                    {(tr.dids || []).length === 0 && (
                        <span className="text-xs text-ink-400 italic">{t('trunk.dids.empty')}</span>
                    )}
                </div>
                <div className="flex gap-2 max-w-sm">
                    <input
                        type="text"
                        value={didInput}
                        onChange={(e) => setDidInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDid(); } }}
                        placeholder={t('trunk.dids.placeholder')}
                        className="flex-1 px-3 py-2 bg-white border border-cream-300 rounded-md font-mono text-sm focus:outline-none focus:border-accent-500"
                    />
                    <Button variant="accent" size="sm" onClick={addDid} disabled={!didInput.trim()}>
                        {t('trunk.dids.add')}
                    </Button>
                </div>
            </div>

            {/* 預設角色（查不到 DID 時的兜底，避免直接掛斷） */}
            <div className="mt-5 pt-4 border-t border-cream-100 max-w-md">
                <label className="block text-xs text-ink-500 mb-1">{t('trunk.defaultAvatar.label')}</label>
                <select
                    value={tr.defaultAvatarId || ''}
                    onChange={(e) => up({ defaultAvatarId: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-cream-300 rounded-md text-sm focus:outline-none focus:border-accent-500"
                >
                    <option value="">{t('trunk.defaultAvatar.none')}</option>
                    {avatars.map((a) => (
                        <option key={a.id} value={a.id}>{a.name || a.id}</option>
                    ))}
                </select>
                <p className="text-xs text-ink-400 mt-1">{t('trunk.defaultAvatar.hint')}</p>
            </div>

            {/* save bar（沿用 TenantEditPage 的 SectionSaveBar 樣式，由父層注入） */}
            {onSave && (
                <div className="mt-4 pt-3 border-t border-cream-100 flex items-center gap-3">
                    <div className="flex-1 text-xs">
                        {dirty && <span className="text-warn-500">{t('saveBar.dirty')}</span>}
                        {!dirty && status?.savedAt && status?.applied !== false && (
                            <span className="text-success-500">{t('saveBar.saved', { time: status.savedAt.toLocaleTimeString() })}</span>
                        )}
                        {!dirty && status?.savedAt && status?.applied === false && (
                            <span className="text-warn-500">{t('saveBar.savedNotReady')}</span>
                        )}
                    </div>
                    <Button variant="accent" size="sm" onClick={onSave} disabled={!dirty || status?.saving || seatMismatch}>
                        {status?.saving ? t('saveBar.saving') : t('trunk.card.save')}
                    </Button>
                </div>
            )}
        </Card>
    );
}
